import { useId, useMemo } from "react";
import styles from "./Icon.module.css";

type IconSize = 16 | 20 | 24 | 48;

interface IconProps {
  readonly svg: string;
  readonly size?: IconSize;
  readonly label?: string;
  readonly className?: string | undefined;
}

export function Icon({ svg: source, size = 20, label, className }: IconProps) {
  const id = `i${useId().replace(/[^\w-]/g, "")}`;
  const svg = useMemo(() => inline(source, id), [source, id]);
  const a11y = label === undefined ? { "aria-hidden": true } : { role: "img", "aria-label": label };
  return (
    <svg
      className={className ? `${styles.icon} ${className}` : styles.icon}
      viewBox={svg.viewBox}
      width={size}
      height={size}
      focusable="false"
      {...a11y}
      dangerouslySetInnerHTML={svg.html}
    />
  );
}

function inline(source: string, id: string): { viewBox: string; html: { __html: string } } {
  const match = /^<svg([^>]*)>([\s\S]*)<\/svg>$/.exec(source.trim());
  if (!match) throw new Error(`an icon of design/icons is not one <svg> element: ${source.slice(0, 60)}`);
  const [, root = "", drawing = ""] = match;
  const viewBox = /\sviewBox="([^"]+)"/.exec(root)?.[1] ?? "0 0 24 24";
  const paint = root.replace(/\s(?:xmlns|width|height|viewBox)="[^"]*"/g, "");
  const own = drawing.replace(/\bid="([^"]+)"/g, `id="${id}-$1"`).replace(/url\(#([^)]+)\)/g, `url(#${id}-$1)`);
  return { viewBox, html: { __html: `<g${paint}>${own}</g>` } };
}
